// Types mirror backend Pydantic schemas (see backend/app/schemas/board.py).
// Update both sides together.

import type { LinkType } from "./ticketLinks";

export type UUID = string;
export type ISODateTime = string;

export type ColumnType =
  | "text"
  | "long_text"
  | "status"
  | "person"
  | "date"
  | "timeline"
  | "number"
  | "checkbox"
  | "tags"
  | "link"
  | "file"
  | "lead_time"
  // Read-only ticket meta — not user-creatable (excluded from COLUMN_TYPES),
  // auto-added to ticket boards by the template / migration.
  | "creator"
  | "creator_department"
  | "creator_company";

export const COLUMN_TYPES: ColumnType[] = [
  "text",
  "long_text",
  "status",
  "person",
  "date",
  "timeline",
  "number",
  "checkbox",
  "tags",
  "link",
  "file",
  "lead_time",
];

/** Vorlaufzeit cell: a duration counted back from the board's goal_date. */
export type LeadTimeUnit = "days" | "weeks" | "months";
export interface LeadTimeValue {
  value: number;
  unit: LeadTimeUnit;
}

export interface UserPublic {
  id: UUID;
  email: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
}

export interface UserMe extends UserPublic {
  keycloak_sub: string;
  roles: string[];
  created_at: ISODateTime;
}

export interface Workspace {
  id: UUID;
  name: string;
  slug: string;
  color: string | null;
  icon: string | null;
  /** Optional company / legal entity (e.g. "LLE" / "LLT"); snapshotted onto
   * tickets created by this workspace's members. */
  company: string | null;
  /** Whether a Teams webhook is set (the URL itself is never returned). */
  teams_configured: boolean;
  /** The system "Persönlich" workspace: private boards (creator-scoped). The FE
   * hides rename/delete/share/members for it and badges its boards as personal. */
  is_personal: boolean;
  /** The requesting user's own membership role in this workspace, or null when
   * they aren't a member (e.g. a flow-admin seeing every workspace, or the
   * memberless Persönlich workspace). Only populated by the per-user workspace
   * list (api.listWorkspaces); used to gate owner/admin-only affordances such as
   * creating a meeting series. */
  role?: WorkspaceRole | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface WorkspaceCreatePayload {
  name: string;
  slug?: string;
}

export interface WorkspaceUpdatePayload {
  name?: string | null;
  slug?: string | null;
  color?: string | null;
  icon?: string | null;
  company?: string | null;
  teams_webhook_url?: string | null;
}

export type BoardKind = "project" | "tickets" | "ideas";

/** Shared, team-wide default view for a board (server-stored on board.view_config). */
// A single shared filter condition. AND-combined across conditions. op + value
// shape depend on the column type: status/tags → "in" + allowed option ids;
// text/long_text → "contains" + substring; checkbox → "is" + bool; date →
// "before"/"after" + ISO date.
export type FilterOp = "in" | "contains" | "is" | "before" | "after";
export interface FilterCond {
  column_id: string;
  op: FilterOp;
  value: string[] | string | boolean | null;
}

export interface SortSpec {
  column_id: string;
  dir: "asc" | "desc";
}

export interface ViewConfig {
  default_view?: "table" | "kanban" | "calendar" | "timeline";
  group_by_column_id?: string | null;
  /** Which status column the Kanban view buckets by (else first status col). */
  kanban_column_id?: string | null;
  hidden_column_ids?: string[];
  /** Shared per-column filters (AND), applied to table + kanban. */
  filters?: FilterCond[];
  /** Shared single-column sort, applied within groups (table) + lanes (kanban). */
  sort?: SortSpec | null;
  /** Ideenbox: user id that receives ideas submitted without a chosen assignee
   * (the "Standard-Zuständige/r"). null/absent → unassigned ideas stay unassigned. */
  default_assignee_user_id?: UUID | null;
}

export interface Board {
  id: UUID;
  workspace_id: UUID;
  name: string;
  description: string | null;
  color: string | null;
  kind: BoardKind;
  submit_enabled: boolean;
  home_listed: boolean;
  view_config: ViewConfig;
  goal_date: string | null;
  ticket_prefix: string | null;
  // F2: when set, the board is archived (hidden from the main lists, restorable).
  archived_at: ISODateTime | null;
  // F1: the current user's custom order position for this board (null = not
  // reordered → default name sort). Per-user; set by the list endpoints.
  order_position: number | null;
  created_by: UUID | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  // How the current user reaches this board: "member" (home workspace),
  // "shared" (a workspace this board is shared into — full access too), or
  // cross-department "guest" (sees only their assigned/created rows). Defaults
  // to member.
  access?: "member" | "shared" | "guest";
  // Whether the requesting user may write to this board (add rows/tickets, edit
  // cells). True for member/shared writers; false for viewers and guests. Set by
  // the board-detail endpoint; absent in list contexts (treated as writable).
  can_write?: boolean;
  // Whether the requesting user is an owner/admin of this board's workspace (or a
  // flow-admin). Lets the row-detail panel keep the requester's Beschreibung
  // editable for admins while it is read-only for ordinary staff. Set by the
  // board-detail endpoint; absent in list contexts (treated as not-admin).
  can_admin?: boolean;
  // True when a meeting series points at this board (its Aufgaben board). DERIVED
  // server-side from meeting_series.aufgaben_board_id — there is no column, so it
  // cannot drift. Drives the Unteraufgaben section, the "Unteraufgabe" wording and
  // the LeftRail filter that keeps an assignee's sidebar free of a ghost board.
  is_aufgaben?: boolean;
}

// A workspace a board is shared into (board-share management dialog).
export interface BoardShare {
  workspace_id: UUID;
  name: string;
  icon: string | null;
  color: string | null;
}

// Per-ticket sharing (Phase 4) — a person or a department a row is shared with.
export interface PersonShare {
  user_id: UUID;
  display_name: string | null;
  username: string | null;
  email: string;
  avatar_url: string | null;
}

export interface DepartmentShare {
  workspace_id: UUID;
  name: string;
  icon: string | null;
  color: string | null;
}

export interface RowShares {
  users: PersonShare[];
  departments: DepartmentShare[];
}

// Phase 5 — Verknüpfte Tickets. One end of a link, resolved for display.
export interface LinkedTicket {
  link_id: UUID;
  link_type: LinkType;
  direction: "outgoing" | "incoming";
  row_id: UUID;
  board_id: UUID;
  ticket_label: string | null;
  title: string;
  status_label: string | null;
  status_color: string | null;
  resolved_at: ISODateTime | null;
}

export interface RowLinks {
  parent: LinkedTicket | null;
  follow_ups: LinkedTicket[];
  related: LinkedTicket[];
}

// The Protokoll section a memo-origin to-do came from — a deep-link breadcrumb
// shown on the row detail panel. Only present (from the backend) for rows whose
// memo the viewer may open, so it never leaks a series/section name.
export interface MemoOrigin {
  memo_id: UUID;
  memo_entry_id: UUID;
  series_name: string;
  meeting_date: string; // ISO date
  block_title: string;
  subheading: string | null;
}

// A ticket offered as a link target in the Verknüpfen picker (same department).
export interface LinkCandidate {
  row_id: UUID;
  board_id: UUID;
  ticket_label: string | null;
  title: string;
}

// An item in the Home "Mit dir geteilt" feed.
export interface SharedTicketItem {
  row_id: UUID;
  board_id: UUID;
  board_name: string;
  board_kind: BoardKind;
  workspace_id: UUID | null;
  workspace_name: string | null;
  workspace_icon: string | null;
  workspace_color: string | null;
  title: string;
  status_label: string | null;
  status_color: string | null;
  share_origin: "person" | "department";
  department_name: string | null;
  created_at: ISODateTime;
  ticket_prefix: string | null;
  ticket_number: number | null;
  resolved_at: ISODateTime | null;
}

export interface BoardCreatePayload {
  name: string;
  description?: string | null;
  color?: string | null;
  kind?: BoardKind;
  home_listed?: boolean;
  goal_date?: string | null;
  ticket_prefix?: string | null;
}

export interface BoardUpdatePayload {
  name?: string | null;
  description?: string | null;
  color?: string | null;
  view_config?: ViewConfig | null;
  goal_date?: string | null;
  ticket_prefix?: string | null;
  home_listed?: boolean | null;
}

export interface BoardSubmitPayload {
  cells: Record<UUID, CellValue>;
}

export interface BoardSubmitResult {
  row_id: UUID;
}

// Stable semantic identity for a status option, independent of its display
// label. Workflow actions + kanban drag target the role, not the label, so
// renaming an option doesn't break them. Mirrors backend actions.py ROLE_*.
export type StatusRole = "open" | "in_progress" | "waiting" | "closed";

export interface StatusOption {
  id: string;
  label: string;
  color: string;
  role?: StatusRole;
  /** Vorlaufzeit: per-section goal date (Zieltermin) when this option is used
   * as a board's section column. Free-form JSONB, no schema change. */
  goal_date?: string;
  /** Archived: kept in the array (so existing cells still resolve + display)
   * but hidden from the editable picker + submit form. Free-form JSONB. */
  archived?: boolean;
}

export interface ColumnConfig {
  options?: StatusOption[];
  allow_multiple?: boolean;
  // Submit-form: a non-workflow single-select (e.g. Bereich) the requester must
  // pick before submitting. Drives both show-on-submit and required validation.
  required?: boolean;
  max_length?: number;
  min?: number;
  max?: number;
  format?: string;
  decimals?: number;
  max_tag_length?: number;
  [extra: string]: unknown;
}

export interface ColumnDef {
  id: UUID;
  board_id: UUID;
  name: string;
  type: ColumnType;
  config: ColumnConfig;
  position: number;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface ColumnCreatePayload {
  name: string;
  type: ColumnType;
  config?: ColumnConfig | null;
  position?: number | null;
}

export interface ColumnUpdatePayload {
  name?: string | null;
  config?: ColumnConfig | null;
  position?: number | null;
}

export type CellValue =
  | string
  | number
  | boolean
  | string[]
  | { url: string; label: string }
  | { start: string; end: string }
  | { value: number; unit: LeadTimeUnit }
  // `file` columns. FileRef is spelled out alongside the loose shape because an
  // INTERFACE never gets an implicit index signature, so FileRef[] is not
  // assignable to Array<{key: string; [k: string]: unknown}> however identical
  // the two look. Without this a file cell can't be written without casting
  // through `unknown`, which would defeat the point of typing it at all.
  | FileRef[]
  | Array<{ key: string; [k: string]: unknown }>
  | null;

export interface Cell {
  row_id: UUID;
  column_def_id: UUID;
  value: CellValue;
  updated_by: UUID | null;
  updated_at: ISODateTime;
}

export interface Row {
  id: UUID;
  board_id: UUID;
  /** Sub-items: parent row id, null = top-level. */
  parent_row_id: UUID | null;
  /** Denormalised tree depth — 0 for top-level, 1 for direct child, etc. */
  depth: number;
  position: number;
  created_by: UUID | null;
  created_by_display_name: string | null;
  created_by_username: string | null;
  // Keycloak sub of the creator — for FE staff-vs-creator gating.
  created_by_keycloak_sub: string | null;
  // Most recent non-creator actor. Default recipient for creator-side actions.
  last_actor_user_id: UUID | null;
  // Number of comments on the row — kanban card badge.
  comment_count: number;
  // Ticketing: per-board human number + when the workflow status hit a closed
  // role (ends the lifetime/age). Null on non-ticket rows / open tickets.
  ticket_number: number | null;
  resolved_at: ISODateTime | null;
  // Ticketing: requester's home department snapshotted at creation (read-only
  // "Abteilung" meta column). Null on non-ticket rows / unknown department.
  creator_department_name: string | null;
  // Ticketing: requester's company snapshotted at creation (read-only
  // "Unternehmen" meta column). Null on non-ticket rows / no company set.
  creator_company_name: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  cells: Cell[];
}

export type WorkspaceRole = "owner" | "admin" | "member" | "viewer";

export interface UserSearchResult {
  id: UUID;
  email: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  keycloak_sub: string;
  department_workspace_id?: UUID | null;
  department_color?: string | null;
}

// @-mention suggestion candidate. Lives here (deps-free) so eager components can
// reference the type without importing the TipTap-heavy mentionSuggestion module.
export interface MentionItem {
  id: string;
  label: string;
}

export interface WorkspaceMemberAddPayload {
  user_id: UUID;
  role?: WorkspaceRole;
}

export interface WorkspaceMemberRoleUpdatePayload {
  role: WorkspaceRole;
}

export interface RowAssignedPayload {
  kind_label: string;
  row_id: UUID;
  board_id: UUID;
  board_name: string;
  row_title: string;
  column_name: string;
  actor_user_id: UUID;
  actor_display_name: string;
  actor_username: string | null;
}

export interface TicketArrivedPayload {
  kind_label: string;
  row_id: UUID;
  board_id: UUID;
  board_name: string;
  row_title: string;
  actor_user_id: UUID;
  actor_display_name: string;
  actor_username: string | null;
}

export interface WorkspaceMember {
  user_id: UUID;
  role: WorkspaceRole;
  joined_at: ISODateTime;
  email: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  department_workspace_id?: UUID | null;
  department_color?: string | null;
}

export type RowActionType =
  | "senden_zur_pruefung"
  | "annehmen"
  | "wartet_auf_kunde"
  | "loesen"
  | "bestaetigen"
  | "erneut_oeffnen"
  // Expanded status ladder (Phase 0). Wire values must match the backend
  // RowActionType StrEnum exactly.
  | "sichten"
  | "einplanen"
  | "in_review"
  | "zurueckstellen"
  // Phase 2: assign-with-message, creator self-close (obsolete), escalate.
  | "zuweisen"
  | "hinfaellig"
  | "eskalieren"
  // Ideenbox (kind='ideas'): reviewer decisions with plan-fixed target status.
  | "annehmen_idee"
  | "ablehnen_idee";

export interface RowActionPayload {
  action: RowActionType;
  body: string;
  recipient_user_ids: UUID[];
  /** Explicit status outcome (e.g. resolve as "Nicht relevant" rather than the
   * first closed option). Omitted → action resolves to the first option for
   * its role. */
  target_option_id?: string;
  /** Annehmen: who the ticket is assigned to (sets the person cell). */
  assignee_user_ids?: UUID[];
}

export interface SubscriptionState {
  subscribed: boolean;
}

export interface RowActionResult {
  row_id: UUID;
  action: RowActionType;
  new_status_id: string | null;
  new_status_label: string | null;
  comment_id: UUID;
  recipient_count: number;
}

export interface BoardDetail extends Board {
  columns: ColumnDef[];
  rows: Row[];
  next_cursor: number | null;
  has_more: boolean;
}

export interface RowCreatePayload {
  position?: number | null;
  /** When set, server computes the new row's position as the midpoint
   * between this row and its next sibling. Powers "Enter on title →
   * next row below" inline-create. */
  after_row_id?: UUID | null;
  /** Sub-items: create the row as a child of this parent. Backend rejects
   * with 422 if it would exceed MAX_ROW_DEPTH. */
  parent_row_id?: UUID | null;
}

export interface RowUpdatePayload {
  position?: number | null;
  /** Reparent (including outdent to top-level via null). When the field is
   * present on the payload, backend recomputes depth. */
  parent_row_id?: UUID | null;
}

export interface CellWritePayload {
  value: CellValue;
}

// Comments ----------------------------------------------------------
export interface Comment {
  id: UUID;
  row_id: UUID;
  user_id: UUID | null;
  /** Reply threading: the comment this one replies to (null = top-level). */
  parent_comment_id?: UUID | null;
  /** Likes: total count + whether the current user liked it. */
  like_count?: number;
  liked_by_me?: boolean;
  author_display_name: string | null;
  author_username: string | null;
  author_avatar_url: string | null;
  author_department_workspace_id?: UUID | null;
  author_department_color?: string | null;
  body: string;
  /** Set when this comment is a workflow-action event (annehmen/loesen/…) →
   * rendered as a styled activity line. Null/undefined = plain comment. */
  action?: RowActionType | null;
  created_at: ISODateTime;
}

export interface CommentCreatePayload {
  body: string;
  parent_comment_id?: UUID | null;
}

// Activity timeline (Phase 3) — the events outbox surfaced as a per-row changelog.
export interface ActivityChange {
  /** set: value set/changed · clear: value removed · assign: person add/remove ·
   * edit: text/long_text content changed (no value shown). */
  kind: "set" | "clear" | "assign" | "edit";
  /** False ⇒ render "zu X" (no old value stored — historical event). */
  has_old: boolean;
  old_label?: string | null;
  new_label?: string | null;
  added?: string[] | null;
  removed?: string[] | null;
}

// One slice of a ticket's cycle-time bar (Zeitleiste): the status it was in
// during [start, end). color is a palette key (null → slate).
export interface StatusSegment {
  status_option_id: string | null;
  color: string | null;
  label: string | null;
  start: ISODateTime;
  end: ISODateTime;
}

/** Map of row id → its cycle-time segments. Empty for non-ticket boards. */
export type BoardStatusSegments = Record<UUID, StatusSegment[]>;

export interface ActivityItem {
  id: UUID;
  occurred_at: ISODateTime;
  event_type:
    | "cell.updated"
    | "row.created"
    | "row.deleted"
    | "row.restored"
    | "row.shared"
    | "row.ticket_linked";
  actor_display_name: string | null;
  actor_username: string | null;
  actor_avatar_url: string | null;
  actor_user_id: UUID | null;
  actor_department_workspace_id?: UUID | null;
  actor_department_color?: string | null;
  column_id?: UUID | null;
  column_name?: string | null;
  column_type?: string | null;
  change?: ActivityChange | null;
}

export interface CommentDeletedPayload {
  id: UUID;
  row_id: UUID;
}

// Realtime envelope mirrors app.core.events.EventEnvelope.
export type WsEventType =
  | "connection.ready"
  | "board.created"
  | "board.updated"
  | "board.deleted"
  | "row.created"
  | "row.updated"
  | "row.deleted"
  | "row.restored"
  | "cell.updated"
  | "column.created"
  | "column.updated"
  | "column.deleted"
  | "comment.created"
  | "comment.deleted"
  | "comment.updated"
  | "presence.heartbeat"
  | "presence.update"
  | "pong";

export interface WsEnvelope<T = unknown> {
  type: WsEventType;
  board_id: UUID;
  payload: T;
  actor_sub: string | null;
  ts: ISODateTime;
}

export interface SearchWorkspaceHit {
  id: UUID;
  name: string;
}

export interface SearchBoardHit {
  id: UUID;
  workspace_id: UUID;
  name: string;
}

export interface SearchRowHit {
  id: UUID;
  board_id: UUID;
  workspace_id: UUID;
  board_name: string;
  title: string;
}

/** Payload of `memo.finalized` / `memo.prep_requested`. 🔴 These carry NO `row_id`/`board_id` —
 *  a memo is not a board — so the memo id travels in the payload and the bell must deep-link
 *  from it. Anything keyed on `board_id` silently does nothing for a memo notification. */
export interface MemoNotificationPayload {
  kind_label: string;
  memo_id: UUID;
  series_id: UUID;
  memo_title: string;
  /** Already German-formatted "13.07.2026" — the series title alone cannot tell two
   *  occurrences apart. */
  memo_date: string | null;
  actor_display_name: string | null;
  actor_username: string | null;
}

export interface SearchCommentHit {
  comment_id: UUID;
  row_id: UUID;
  board_id: UUID;
  workspace_id: UUID;
  board_name: string;
  row_title: string;
  excerpt: string;
}

export interface SearchSeriesHit {
  series_id: UUID;
  name: string;
}

export interface SearchMemoHit {
  memo_id: UUID;
  series_id: UUID;
  series_name: string;
  meeting_date: string; // ISO yyyy-mm-dd
  section_title: string | null;
  entry_id: UUID | null;
  block_id: UUID | null;
  excerpt: string;
  retired: boolean;
  more_count: number;
}

export interface SearchResponse {
  workspaces: SearchWorkspaceHit[];
  boards: SearchBoardHit[];
  rows: SearchRowHit[];
  comments: SearchCommentHit[];
  series: SearchSeriesHit[];
  memos: SearchMemoHit[];
}

export interface OpenWorkItem {
  row_id: UUID;
  board_id: UUID;
  board_name: string;
  board_kind: BoardKind;
  workspace_id: UUID | null;
  workspace_name: string | null;
  workspace_icon: string | null;
  workspace_color: string | null;
  title: string;
  status_label: string | null;
  status_color: string | null;
  kind: "ticket" | "task" | string;
  created_at: ISODateTime;
  // F7: the row's due date (YYYY-MM-DD) from its date column, for the Home calendar.
  due: string | null;
  ticket_prefix: string | null;
  ticket_number: number | null;
  resolved_at: ISODateTime | null;
}

// F7: a dated row on one of the user's boards — the Home calendar widget's feed
// (aggregated across boards, not gated on assignment).
export interface HomeCalendarItem {
  row_id: UUID;
  board_id: UUID;
  board_name: string;
  title: string;
  due: string;
  status_label: string | null;
  status_color: string | null;
  workspace_name: string | null;
  workspace_icon: string | null;
  workspace_color: string | null;
}

// Cross-department submit target ("Anfrage stellen" card) — carries the owning
// workspace's name/icon/color since the user isn't a member of it.
// A person who can appear in a board's person cells — workspace member or guest.
// Used for avatar/name resolution (guests aren't in the workspace-members list).
export interface BoardPerson {
  user_id: UUID;
  display_name: string | null;
  username: string | null;
  email: string;
  avatar_url: string | null;
  is_guest: boolean;
  department_workspace_id?: UUID | null;
  department_color?: string | null;
}

export interface RequestForm {
  id: UUID;
  workspace_id: UUID;
  name: string;
  workspace_name: string;
  workspace_icon: string | null;
  workspace_color: string | null;
}

export interface MySubmissionItem {
  row_id: UUID;
  board_id: UUID;
  workspace_id: UUID;
  board_name: string;
  board_kind: BoardKind;
  title: string;
  created_at: ISODateTime;

  current_status_id: string | null;
  current_status_label: string | null;
  current_status_color: string | null;

  last_comment_at: ISODateTime | null;
  last_comment_body_excerpt: string | null;
  last_comment_author_display_name: string | null;
  last_comment_author_username: string | null;

  unread_notification_count: number;
  comment_count: number;

  ticket_prefix: string | null;
  ticket_number: number | null;
  resolved_at: ISODateTime | null;

  last_actor_user_id: UUID | null;
}

export interface StatusChangedPayload {
  kind_label: string;
  row_id: UUID;
  board_id: UUID;
  board_name: string;
  row_title: string;
  from_status_id: string | null;
  from_status_label: string | null;
  to_status_id: string;
  to_status_label: string;
  actor_user_id: UUID;
  actor_display_name: string;
  actor_username: string | null;
}

export interface RowCommentedPayload {
  kind_label: string;
  comment_id: UUID;
  row_id: UUID;
  board_id: UUID;
  board_name: string;
  row_title: string;
  body_excerpt: string;
  actor_user_id: UUID;
  actor_display_name: string;
  actor_username: string | null;
}

export interface DeadlineApproachingPayload {
  kind_label: string;
  row_id: UUID;
  board_id: UUID;
  board_name: string;
  row_title: string;
  deadline: string;
  days_remaining: number;
}

export interface RowSharedPayload {
  kind_label: string;
  row_id: UUID;
  board_id: UUID;
  board_name: string;
  row_title: string;
  actor_user_id: UUID;
  actor_display_name: string;
  actor_username: string | null;
  /** Department name when shared with a whole workspace; null for a person share. */
  via_department: string | null;
}

export interface FollowUpCreatedPayload {
  kind_label: string;
  row_id: UUID; // the MAIN ticket (where the Folgeticket shows)
  board_id: UUID;
  board_name: string;
  row_title: string;
  follow_up_label: string | null; // the new Folgeticket's "IT-43"
  actor_user_id: UUID;
  actor_display_name: string;
  actor_username: string | null;
}

export interface NotificationItem {
  id: UUID;
  kind:
    | "row.status_changed"
    | "row.commented"
    | "row.assigned"
    | "row.deadline_approaching"
    | "row.deadline_due"
    | "row.deadline_overdue"
    | "row.ticket_arrived"
    | "row.shared"
    | "row.follow_up_created"
    | string;
  payload:
    | StatusChangedPayload
    | RowCommentedPayload
    | RowAssignedPayload
    | DeadlineApproachingPayload
    | TicketArrivedPayload
    | RowSharedPayload
    | FollowUpCreatedPayload
    | Record<string, unknown>;
  row_id: UUID | null;
  board_id: UUID | null;
  read_at: ISODateTime | null;
  created_at: ISODateTime;
}

// File attachments (file column cell value entries + upload response) --
export interface FileRef {
  key: string;
  name: string;
  size: number;
  content_type: string;
  url: string;
}

// Parsed .eml/.msg content for the in-app mail viewer (GET /api/mail/{key}).
export interface MailAttachment {
  filename: string;
  size: number;
  content_type: string;
}

export interface MailContent {
  from: string;
  to: string;
  cc: string;
  date: string;
  subject: string;
  body_html: string | null;
  body_text: string | null;
  has_remote_content: boolean;
  attachments: MailAttachment[];
}

// Monday.com migration ------------------------------------------------
export interface MondayBoardSummary {
  id: string;
  name: string;
  items_count: number | null;
  description: string | null;
}

export interface MondayStatusOption {
  id: string;
  label: string;
  color: string;
}

// One editable Monday source column in the import mapping modal.
export interface MondayPlanColumn {
  key: string;
  origin: "parent" | "subitem";
  monday_id: string;
  monday_title: string;
  monday_type: string;
  include: boolean;
  flow_type: ColumnType | null;
  flow_name: string;
  own_name: string;
  merge_into: string | null;
  is_primary: boolean;
  note: string | null;
  status_options: MondayStatusOption[] | null;
}

// A column the importer always creates (Name, Gruppe) — shown read-only.
export interface MondaySyntheticColumn {
  name: string;
  flow_type: string;
  options?: MondayStatusOption[] | null;
}

export interface MondayBoardPlan {
  monday_board_id: string;
  name: string | null;
  groups_count: number;
  synthetic: MondaySyntheticColumn[];
  columns: MondayPlanColumn[];
  overridable_types: string[];
}

export interface MondayColumnOverride {
  key: string;
  include: boolean;
  name: string;
  flow_type: string | null;
  merge_into: string | null;
}

export interface MondayImportRequestPayload {
  token: string;
  monday_board_id: string;
  workspace_id: UUID;
  import_subitems: boolean;
  import_comments: boolean;
  import_files: boolean;
  column_overrides: MondayColumnOverride[];
}

export interface MondayImportReport {
  monday_board_id: string;
  board_name: string;
  flow_board_id: string | null;
  columns_created: number;
  rows: number;
  subitems: number;
  cells: number;
  comments: number;
  files_imported: number;
  files_failed: number;
  cell_errors: number;
  degraded_columns: string[];
  skipped_columns: string[];
  unmatched_people: string[];
  unmatched_comment_authors: string[];
}

export type NotificationChannel = "in_app" | "email" | "teams";

export interface NotificationPrefs {
  matrix: Record<string, Partial<Record<NotificationChannel, boolean>>>;
  always_notify_directed: boolean;
  // Admin-locked (obligatory) cells — forced on, the UI greys them.
  locked?: Record<string, Partial<Record<NotificationChannel, boolean>>>;
  reasons: string[];
  channels: NotificationChannel[];
}

// Org-wide admin defaults: the default matrix all users inherit + the locked map.
export interface NotificationDefaults {
  matrix: Record<string, Partial<Record<NotificationChannel, boolean>>>;
  locked: Record<string, Partial<Record<NotificationChannel, boolean>>>;
  reasons: string[];
  channels: NotificationChannel[];
}

/** Admin backup panel — metadata only; the API never returns a path. */
export interface BackupFile {
  name: string;
  size_bytes: number;
  modified_at: string;
}

export interface BackupsOverview {
  configured: boolean;
  is_stale: boolean;
  newest_at: string | null;
  count: number;
  total_bytes: number;
  files: BackupFile[];
}

// --- Meeting memos (Protokolle) -------------------------------------------
export type MemoStatus = "draft" | "finalized";

// A resolved user reference for memo/series participants + editors, and
// series-task assignees. Mirrors the person-picker shape used elsewhere.
export interface MemoUser {
  id: UUID;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  department_workspace_id?: UUID | null;
  department_color?: string | null;
}

// A row surfaced on the series's linked Aufgaben board (Aufgaben tab).
export interface SeriesTask {
  row_id: UUID;
  title: string;
  status_label: string | null;
  status_role: string | null;
  done: boolean;
  assignee: MemoUser | null;
  due: string | null;
  solution: string | null;
  // Origin of the linked Aufgaben row: earliest memo's date + that entry's section.
  origin_memo_date: string | null;
  origin_section: string | null;
}

export interface MeetingSeries {
  id: UUID;
  home_workspace_id: UUID;
  name: string;
  description: string | null;
  aufgaben_board_id: UUID | null;
  default_weekday?: number | null;
  default_start_time?: string | null;
  section_template: string[];
  default_participant_ids: UUID[];
  editor_user_ids: UUID[];
  archived_at: string | null;
  access: MemoAccessFlags;
  participants: MemoUser[];
  editors: MemoUser[];
  // Read-only enrichments for the Übersicht list.
  last_memo_date: string | null;
  open_task_count: number;
}

export interface MeetingSeriesCreatePayload {
  home_workspace_id: UUID;
  name: string;
  description?: string | null;
  default_weekday?: number | null;
  default_start_time?: string | null;
  section_template?: string[];
  default_participant_ids?: UUID[];
  editor_user_ids?: UUID[];
}

/** `update` = board activity (a comment, a moved Frist) materialised into the
 *  minutes under the to-do it reports on. It is never a task and never a
 *  presentation focus stop; see components/memo/MemoUpdateRow.tsx. */
export type MemoEntryKind = "point" | "subheading" | "update";

export interface MemoEntry {
  id: UUID;
  block_id: UUID;
  position: number;
  kind: MemoEntryKind;
  log_date: string | null;
  body: string;
  retired: boolean;
  retired_at?: string | null;
  assignee_user_id: UUID | null;
  due_date: string | null;
  row_id: UUID | null;
  task_done: boolean;
  task_solution: string | null;
  /** Set when this entry was copied in from an earlier meeting. Drives the
   *  history lock (see lib/memoEditLock.ts); enforced server-side. */
  carried_from_entry_id?: string | null;
  /** Client-only: set by historyToEntry on entries merged in from earlier meetings.
   *  Marks a read-only, past-dated line so the row hides its controls and always
   *  shows the closure badge, even inside a draft. Never sent by/to the backend. */
  is_historical?: boolean;
  /** An update (kind="update") hangs off the to-do it reports on. */
  parent_entry_id?: string | null;
  /** The board event an update was materialised from ("comment:<id>" / "due:<id>"). */
  source_ref?: string | null;
  /** Who wrote the line — for an update, the person who commented on the board or
   *  moved the Frist. The attribution is why an update is read-only. */
  created_by?: UUID | null;
  /** Sub-item rollup of the to-do's linked board row: how many sub-rows exist and
   *  how many are closed. Both 0 when the row has none (or the entry is not a
   *  to-do) — the Protokoll then shows nothing. A COUNT only, on purpose: the
   *  meeting holds ONE person to the to-do, so sub-assignees are never listed. */
  subtask_total?: number;
  subtask_done?: number;
  history_count?: number;
  latest_closed_at?: string | null;
}

export interface SectionHistoryEntry {
  id: UUID;
  body: string;
  kind: MemoEntryKind;
  log_date: string | null;
  retired_at: string | null;
  assignee_user_id: UUID | null;
  task_done: boolean;
  position: number;
  source_memo_id: UUID;
  source_memo_date: string;
}

export interface MemoBlock {
  id: UUID;
  title: string;
  position: number;
  entries: MemoEntry[];
  history_count?: number;
  latest_closed_at?: string | null;
}

export interface MemoAccessFlags {
  can_edit_draft: boolean;
  can_edit_finalized: boolean;
  can_admin: boolean;
}

export interface Memo {
  id: UUID;
  series_id: UUID;
  title: string;
  meeting_date: string;
  start_time: string | null;
  end_time: string | null;
  status: MemoStatus;
  participant_ids: UUID[];
  participants: MemoUser[];
  previous_memo_id: UUID | null;
  blocks: MemoBlock[];
  access: MemoAccessFlags;
  // "Um Beiträge bitten" already sent for this draft (who + when) → the button shows a
  // sent state so nobody re-mails the roster. Null = not yet requested.
  prep_requested_at: string | null;
  prep_requested_by: string | null;
}

export interface MemoSummary {
  id: UUID;
  title: string;
  meeting_date: string;
  status: MemoStatus;
  /** "HH:MM:SS" local wall clock, or null — the series list moves today's meeting
   *  to "Vergangen" once it has started. */
  start_time?: string | null;
  // Read-only enrichments for the Protokolle list.
  section_count: number;
  open_task_count: number;
  participant_count: number;
}

export interface MemoHeaderPayload {
  meeting_date: string;
  start_time?: string | null;
  end_time?: string | null;
  participant_ids?: UUID[] | null;
}

export interface MemoAuditEntry {
  id: UUID;
  actor_user_id: UUID | null;
  actor_name: string | null;
  action: string;
  detail: Record<string, unknown>;
  created_at: string;
}
